import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckSquare, Building2, Layers, Users, FileWarning, BarChart3,
  IndianRupee, BookOpen, Plus, Pencil, Trash2, Eye, EyeOff,
  Upload, X, Tag, Globe, FileText, MessageCircle, Ticket, Users2, Contact2
} from "lucide-react";
import WhatsAppFAQAdmin from "@/components/Whatsappfaqadmin";
import CrmPanel from "@/components/crm/CrmPanel";
import CouponBatches from "@/components/crm/CouponBatches";

// ─── existing sub-pages (unchanged) ───────────────────────────────────────────

function Overview() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  const cards = [
    { label: "Pending Jobs", value: stats.pending_jobs, color: "bg-yellow-50 text-yellow-700" },
    { label: "Approved Today", value: stats.approved_today, color: "bg-green-50 text-green-700" },
    { label: "Total Users", value: stats.total_users, color: "bg-blue-50 text-blue-700" },
    { label: "New Today", value: stats.new_users_today, color: "bg-purple-50 text-purple-700" },
  ];
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Admin Dashboard</h2>
      <div className="grid md:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className={`p-5 rounded-xl border border-slate-200 ${c.color}`} data-testid={`stat-${c.label.toLowerCase().replace(' ','-')}`}>
            <div className="text-3xl font-bold">{c.value ?? 0}</div>
            <div className="text-sm mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/admin/approvals"><Button className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="quick-review-btn">Review Pending Jobs</Button></Link>
        <Link to="/admin/cities"><Button variant="outline">Add City</Button></Link>
        <Link to="/admin/categories"><Button variant="outline">Add Category</Button></Link>
        <Link to="/admin/coupons"><Button variant="outline"><Ticket className="w-4 h-4 mr-2" />Add Coupon</Button></Link>
        <Link to="/admin/blogs"><Button variant="outline"><BookOpen className="w-4 h-4 mr-2" />Write Blog Post</Button></Link>
        <Link to="/admin/whatsapp"><Button variant="outline"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp FAQ</Button></Link>
        <Link to="/admin/banner"><Button variant="outline">Change Banner</Button></Link>
      </div>
    </div>
  );
}

function Approvals() {
  const [jobs, setJobs] = useState([]);
  const [reviewJob, setReviewJob] = useState(null);
  const [reason, setReason] = useState("");
  const refresh = () => api.get("/admin/jobs/pending").then(r => setJobs(r.data));
  useEffect(() => { refresh(); }, []);
  const decide = async (action) => {
    try {
      await api.post(`/admin/jobs/${reviewJob.id}/review`, { action, reason });
      toast.success(`Job ${action}d`);
      setReviewJob(null); setReason("");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="approvals-page">
      <h2 className="text-2xl font-bold mb-6">Job Approval Queue</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Employer</TableHead><TableHead>City</TableHead><TableHead>Submitted</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {jobs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500">No pending jobs 🎉</TableCell></TableRow>}
            {jobs.map(j => (
              <TableRow key={j.id}>
                <TableCell><div className="font-medium">{j.title}</div><div className="text-xs text-slate-500">{j.category}</div></TableCell>
                <TableCell>{j.business_name}</TableCell>
                <TableCell>{j.city}</TableCell>
                <TableCell className="text-sm text-slate-500">{(j.created_at || "").slice(0,10)}</TableCell>
                <TableCell className="text-right"><Button size="sm" onClick={() => setReviewJob(j)} data-testid={`review-${j.id}`}>Review</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!reviewJob} onOpenChange={(o) => !o && setReviewJob(null)}>
        <DialogContent className="max-w-2xl">
          {reviewJob && (
            <>
              <DialogHeader><DialogTitle>{reviewJob.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm max-h-[400px] overflow-y-auto">
                <div><b>Business:</b> {reviewJob.business_name} ({reviewJob.business_type})</div>
                <div><b>City:</b> {reviewJob.city} · <b>Type:</b> {reviewJob.job_type}</div>
                <div><b>Salary:</b> {reviewJob.show_salary ? `₹${reviewJob.salary_min} - ₹${reviewJob.salary_max}` : "Hidden"}</div>
                <div className="pt-2 whitespace-pre-wrap"><b>Description:</b> {reviewJob.description}</div>
              </div>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Optional rejection reason..." />
              <DialogFooter>
                <Button variant="outline" className="text-red-600" onClick={() => decide("reject")} data-testid="reject-btn">Reject</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => decide("approve")} data-testid="approve-btn">Approve</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Cities() {
  const [cities, setCities] = useState([]);
  const [name, setName] = useState(""); const [state, setState] = useState("");
  const refresh = () => api.get("/cities?all_cities=true").then(r => setCities(r.data));
  useEffect(() => { refresh(); }, []);
  const add = async () => {
    if (!name) return;
    try { await api.post("/cities", { name, state, enabled: true }); setName(""); setState(""); refresh(); toast.success("Added"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const toggle = async (c) => { await api.put(`/cities/${c.id}`, { ...c, enabled: !c.enabled }); refresh(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/cities/${id}`); refresh(); } };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">City Management</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex gap-2 mb-4">
        <Input placeholder="City name" value={name} onChange={e => setName(e.target.value)} data-testid="city-name-input" />
        <Input placeholder="State" value={state} onChange={e => setState(e.target.value)} />
        <Button onClick={add} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="add-city-btn">Add</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>State</TableHead><TableHead>Jobs</TableHead><TableHead>Enabled</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {cities.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.state}</TableCell>
                <TableCell>{c.job_count}</TableCell>
                <TableCell><Switch checked={c.enabled} onCheckedChange={() => toggle(c)} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => del(c.id)}>Delete</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Categories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const refresh = () => api.get("/categories?all_cats=true").then(r => setItems(r.data));
  useEffect(() => { refresh(); }, []);
  const add = async () => {
    if (!name) return;
    try { await api.post("/categories", { name, icon: "Briefcase", enabled: true, order: items.length }); setName(""); refresh(); toast.success("Added"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const toggle = async (c) => { await api.put(`/categories/${c.id}`, { ...c, enabled: !c.enabled }); refresh(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/categories/${id}`); refresh(); } };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Category Management</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex gap-2 mb-4">
        <Input placeholder="Category name" value={name} onChange={e => setName(e.target.value)} data-testid="cat-name-input" />
        <Button onClick={add} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="add-cat-btn">Add</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Jobs</TableHead><TableHead>Enabled</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.job_count}</TableCell>
                <TableCell><Switch checked={c.enabled} onCheckedChange={() => toggle(c)} /></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => del(c.id)}>Delete</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UsersList() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const refresh = () => api.get("/admin/users", { params: q ? { q } : {} }).then(r => setUsers(r.data));
  useEffect(() => { refresh(); }, []);
  const toggleBlock = async (u) => {
    await api.post(`/admin/users/${u.id}/block`, null, { params: { blocked: !u.blocked } });
    refresh();
  };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">User Management</h2>
      <div className="flex gap-2 mb-4">
        <Input placeholder="Search by name or email" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && refresh()} />
        <Button onClick={refresh} variant="outline">Search</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell>{u.blocked ? <Badge className="badge-rejected">Blocked</Badge> : <Badge className="badge-approved">Active</Badge>}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => toggleBlock(u)}>{u.blocked ? "Unblock" : "Block"}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Reports() {
  const [reports, setReports] = useState([]);
  const refresh = () => api.get("/admin/reports").then(r => setReports(r.data));
  useEffect(() => { refresh(); }, []);
  const resolve = async (id, remove) => {
    await api.post(`/admin/reports/${id}/resolve`, null, { params: { remove_job: remove } });
    refresh();
  };
  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Reported Jobs</h2>
      <div className="space-y-3">
        {reports.length === 0 && <div className="text-slate-500">No reports.</div>}
        {reports.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-semibold">{r.job?.title || "(deleted)"}</div>
            <div className="text-sm text-slate-600">Reason: {r.reason}</div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => resolve(r.id, false)}>Keep</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => resolve(r.id, true)}>Remove Job</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompaniesAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [reviewing, setReviewing] = useState(null);
  const [reason, setReason] = useState("");
  const refresh = () => api.get("/admin/companies", { params: filter === "all" ? {} : { status: filter } }).then(r => setItems(r.data));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);
  const decide = async (action) => {
    try {
      await api.post(`/admin/companies/${reviewing.company_id}/review`, { action, reason });
      toast.success(`Company ${action}d`);
      setReviewing(null); setReason(""); refresh();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="companies-admin">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Companies</h2>
          <p className="text-slate-500 text-sm">Approve company profiles before they can post jobs.</p>
        </div>
        <div className="flex gap-2">
          {["pending","approved","rejected","all"].map(f => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} className={filter === f ? "bg-[#1D6FF2] hover:bg-[#1659C7]" : ""}>{f}</Button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Company ID</TableHead><TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>Website</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No companies in this view.</TableCell></TableRow>}
            {items.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs">{c.company_id}</TableCell>
                <TableCell className="font-medium">{c.name}<div className="text-xs text-slate-500">{c.industry}</div></TableCell>
                <TableCell>{c.city}</TableCell>
                <TableCell>{c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-[#1D6FF2] text-xs">{c.website.slice(0,30)}</a> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell><Badge className={c.status === "approved" ? "badge-approved" : c.status === "rejected" ? "badge-rejected" : "badge-pending"}>{c.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {c.status === "pending" ? (
                    <Button size="sm" onClick={() => setReviewing(c)} data-testid={`review-company-${c.company_id}`}>Review</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setReviewing(c)}>View</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="max-w-2xl">
          {reviewing && (
            <>
              <DialogHeader><DialogTitle>{reviewing.name} <Badge variant="outline" className="font-mono text-xs ml-2">{reviewing.company_id}</Badge></DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm max-h-[400px] overflow-y-auto">
                <div><b>Industry:</b> {reviewing.industry || "—"} · <b>Size:</b> {reviewing.size || "—"}</div>
                <div><b>City:</b> {reviewing.city || "—"} · <b>Founded:</b> {reviewing.founded_year || "—"}</div>
                <div><b>Contact:</b> {reviewing.email} · {reviewing.mobile}</div>
                <div><b>Website:</b> {reviewing.website || "(not provided)"}</div>
                <div><b>Address:</b> {reviewing.address || "—"}</div>
                <div className="pt-2 whitespace-pre-wrap"><b>About:</b> {reviewing.description || "—"}</div>
              </div>
              {reviewing.status === "pending" && (
                <>
                  <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Optional rejection reason..." />
                  <DialogFooter>
                    <Button variant="outline" className="text-red-600" onClick={() => decide("reject")} data-testid="reject-company-btn">Reject</Button>
                    <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => decide("approve")} data-testid="approve-company-btn">Approve</Button>
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PricingAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [edits, setEdits] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [newAmt, setNewAmt] = useState("");
  const [adding, setAdding] = useState(false);

  const refresh = () => api.get("/pricing").then(r => setItems(r.data));

  useEffect(() => {
    refresh();
    api.get("/categories?all_cats=true").then(r => setCategories(r.data));
  }, []);

  // Categories not yet in pricing list
  const availableCategories = categories.filter(
    c => !items.some(p => p.category === c.name)
  );

  const startEdit = (cat, currentAmt) => {
    setEditingRow(cat);
    setEdits(s => ({ ...s, [cat]: String(currentAmt) }));
  };

  const cancelEdit = () => setEditingRow(null);

  const save = async (cat) => {
    try {
      const amt = parseFloat(edits[cat] ?? 0);
      if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
      await api.put("/pricing", { category: cat, amount: amt });
      toast.success("Saved");
      setEditingRow(null);
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (cat) => {
    if (!window.confirm(`Delete pricing for "${cat}"?`)) return;
    try {
      await api.delete("/pricing", { data: { category: cat } });
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const addNew = async () => {
    if (!newCat) { toast.error("Please select a category"); return; }
    const amt = parseFloat(newAmt);
    if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.put("/pricing", { category: newCat, amount: amt });
      toast.success("Added");
      setNewCat(""); setNewAmt(""); setAdding(false);
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="pricing-admin">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold">Job Posting Pricing</h2>
        <Button
          size="sm"
          onClick={() => { setAdding(a => !a); setNewCat(""); setNewAmt(""); }}
          className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Pricing
        </Button>
      </div>
      <p className="text-slate-500 text-sm mb-6">
        First post is always free. Set the price each company pays for subsequent posts per category.
      </p>

      {/* Add new row */}
      {adding && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-wrap gap-3 items-end mb-4">
          <div className="flex-1 min-w-[180px] space-y-1">
            <Label className="text-xs text-slate-600">Category</Label>
            <select
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#1D6FF2]"
            >
              <option value="">Select a category…</option>
              {availableCategories.length === 0 && (
                <option disabled>All categories already have pricing</option>
              )}
              {availableCategories.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="w-36 space-y-1">
            <Label className="text-xs text-slate-600">Amount (₹)</Label>
            <Input
              type="number"
              placeholder="0"
              value={newAmt}
              onChange={e => setNewAmt(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addNew()}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={addNew}
              disabled={!newCat || availableCategories.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              Save
            </Button>
            <Button variant="outline" onClick={() => { setAdding(false); setNewCat(""); setNewAmt(""); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Amount (₹)</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-10 text-slate-400">
                  No pricing entries yet.
                </TableCell>
              </TableRow>
            )}
            {items.map(p => (
              <TableRow key={p.category}>
                <TableCell className="font-medium">{p.category}</TableCell>
                <TableCell>
                  {editingRow === p.category ? (
                    <Input
                      type="number"
                      value={edits[p.category] ?? p.amount}
                      onChange={e => setEdits(s => ({ ...s, [p.category]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === "Enter") save(p.category);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="w-32"
                      autoFocus
                      data-testid={`pricing-input-${p.category}`}
                    />
                  ) : (
                    <span className="text-slate-700">₹{p.amount}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {editingRow === p.category ? (
                      <>
                        <Button size="sm" onClick={() => save(p.category)} className="bg-green-600 hover:bg-green-700 text-white">
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="icon" variant="ghost" title="Edit"
                          onClick={() => startEdit(p.category, p.amount)}
                          data-testid={`edit-pricing-${p.category}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" title="Delete"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => del(p.category)}
                          data-testid={`delete-pricing-${p.category}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── COUPONS ADMIN ────────────────────────────────────────────────────────────

function CouponsAdmin() {
  const [coupons, setCoupons] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const blank = {
    code: "", discount_type: "flat", discount_value: "", max_discount: "",
    min_amount: "", company_id: "", city: "", category: "", user_email: "",
    max_uses: "", per_user_limit: "", expires_at: "", active: true, note: "",
  };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const refresh = () => api.get("/admin/coupons").then(r => setCoupons(r.data)).catch(() => {});
  useEffect(() => {
    refresh();
    api.get("/cities?all_cities=true").then(r => setCities(r.data)).catch(() => {});
    api.get("/categories?all_cats=true").then(r => setCategories(r.data)).catch(() => {});
    api.get("/admin/companies").then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, discount_type: c.discount_type || "flat",
      discount_value: c.discount_value ?? "", max_discount: c.max_discount ?? "",
      min_amount: c.min_amount ?? "", company_id: c.company_id || "",
      city: c.city || "", category: c.category || "", user_email: c.user_email || "",
      max_uses: c.max_uses ?? "", per_user_limit: c.per_user_limit ?? "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
      active: c.active ?? true, note: c.note || "",
    });
    setOpen(true);
  };

  const genCode = async () => {
    try { const r = await api.get("/admin/coupons/generate-code"); set("code", r.data.code); }
    catch { toast.error("Could not generate code"); }
  };

  const num = (v) => (v === "" || v === null ? null : Number(v));

  const save = async () => {
    if (!form.code.trim()) { toast.error("Enter or generate a coupon code"); return; }
    if (form.discount_value === "" || Number(form.discount_value) <= 0) {
      toast.error("Enter a valid discount value"); return;
    }
    if (form.discount_type === "percent" && Number(form.discount_value) > 100) {
      toast.error("Percentage cannot exceed 100"); return;
    }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      max_discount: num(form.max_discount),
      min_amount: num(form.min_amount) ?? 0,
      company_id: form.company_id || "",
      city: form.city || "",
      category: form.category || "",
      user_email: form.user_email.trim() || "",
      max_uses: num(form.max_uses),
      per_user_limit: num(form.per_user_limit),
      expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
      active: form.active,
      note: form.note || "",
    };
    try {
      if (editing) { await api.put(`/admin/coupons/${editing.id}`, payload); toast.success("Coupon updated"); }
      else { await api.post("/admin/coupons", payload); toast.success("Coupon created"); }
      setOpen(false); refresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/admin/coupons/${c.id}`, { active: !c.active }); refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const scopeLabel = (c) => {
    const parts = [];
    if (c.company_id) parts.push(`🏢 ${c.company_id}`);
    if (c.city) parts.push(`📍 ${c.city}`);
    if (c.category) parts.push(`🏷️ ${c.category}`);
    if (c.user_email) parts.push(`👤 ${c.user_email}`);
    return parts.length ? parts : ["Anyone (global)"];
  };

  const selectCls = "w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#1D6FF2]";

  return (
    <div data-testid="coupons-admin">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold">Coupon Codes</h2>
        <Button size="sm" onClick={openNew} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Coupon
        </Button>
      </div>
      <p className="text-slate-500 text-sm mb-6">
        Create discounts that apply to the job-posting total. Leave all targeting fields blank for a global code,
        or restrict a coupon to one company, city, category or user.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Applies to</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No coupons yet.</TableCell></TableRow>
            )}
            {coupons.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                <TableCell>{c.discount_type === "percent"
                  ? `${c.discount_value}%${c.max_discount ? ` (max ₹${c.max_discount})` : ""}`
                  : `₹${c.discount_value}`}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-[260px]">
                    {scopeLabel(c).map((p, i) => (
                      <span key={i} className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded">{p}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {c.used_count ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                </TableCell>
                <TableCell className="text-sm text-slate-500">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</TableCell>
                <TableCell><Switch checked={c.active} onCheckedChange={() => toggleActive(c)} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.code}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" title="Delete" className="text-red-500 hover:bg-red-50" onClick={() => del(c)} data-testid={`delete-coupon-${c.code}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : "New Coupon"}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Coupon Code *</Label>
              <div className="flex gap-2">
                <Input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME50" className="font-mono uppercase" disabled={!!editing} data-testid="coupon-code-input" />
                {!editing && <Button type="button" variant="outline" onClick={genCode}>Random</Button>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Discount Type</Label>
              <select className={selectCls} value={form.discount_type} onChange={e => set("discount_type", e.target.value)}>
                <option value="flat">Flat (₹)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>{form.discount_type === "percent" ? "Discount (%)" : "Discount Amount (₹)"} *</Label>
              <Input type="number" value={form.discount_value} onChange={e => set("discount_value", e.target.value)} placeholder="0" />
            </div>

            {form.discount_type === "percent" && (
              <div className="space-y-1.5">
                <Label>Max Discount Cap (₹)</Label>
                <Input type="number" value={form.max_discount} onChange={e => set("max_discount", e.target.value)} placeholder="optional" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Min Order Total (₹)</Label>
              <Input type="number" value={form.min_amount} onChange={e => set("min_amount", e.target.value)} placeholder="0" />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Targeting — leave blank to apply to anyone</p>
            </div>

            <div className="space-y-1.5">
              <Label>Specific Company</Label>
              <select className={selectCls} value={form.company_id} onChange={e => set("company_id", e.target.value)}>
                <option value="">Any company</option>
                {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name} ({c.company_id})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Specific City</Label>
              <select className={selectCls} value={form.city} onChange={e => set("city", e.target.value)}>
                <option value="">Any city</option>
                {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Specific Category (post type)</Label>
              <select className={selectCls} value={form.category} onChange={e => set("category", e.target.value)}>
                <option value="">Any category</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Individual User (email)</Label>
              <Input type="email" value={form.user_email} onChange={e => set("user_email", e.target.value)} placeholder="any user" />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Limits</p>
            </div>

            <div className="space-y-1.5">
              <Label>Max Total Uses</Label>
              <Input type="number" value={form.max_uses} onChange={e => set("max_uses", e.target.value)} placeholder="unlimited" />
            </div>
            <div className="space-y-1.5">
              <Label>Per-User Limit</Label>
              <Input type="number" value={form.per_user_limit} onChange={e => set("per_user_limit", e.target.value)} placeholder="unlimited" />
            </div>
            <div className="space-y-1.5">
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expires_at} onChange={e => set("expires_at", e.target.value)} />
            </div>
            <div className="space-y-1.5 flex items-end gap-3">
              <Label className="mb-0">Active</Label>
              <Switch checked={form.active} onCheckedChange={v => set("active", v)} />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label>Note (internal)</Label>
              <Input value={form.note} onChange={e => set("note", e.target.value)} placeholder="e.g. Diwali campaign" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="save-coupon-btn">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Coupon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── BLOG ADMIN ──────────────────────────────────────────────────────────────

/* Simple tag-input component */
function TagInput({ tags, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim().toLowerCase();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput("");
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input
          placeholder="Add tag…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="h-8 text-sm"
        />
        <Button type="button" size="sm" variant="outline" onClick={add}>Add</Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs font-medium">
            {t}
            <button type="button" onClick={() => remove(t)} className="hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

/* Image upload helper */
function CoverUpload({ value, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
          <img src={value} alt="cover" className="w-full h-44 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button type="button" onClick={() => fileRef.current.click()} className="bg-white text-slate-800 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-slate-100">Change</button>
            <button type="button" onClick={() => onChange("")} className="bg-red-500 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-red-600">Remove</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current.click()}
          className="w-full h-36 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-[#1D6FF2] hover:text-[#1D6FF2] hover:bg-blue-50 transition-colors"
        >
          {uploading ? (
            <div className="w-6 h-6 border-2 border-[#1D6FF2] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6" />
              <span className="text-sm font-medium">Upload cover image</span>
              <span className="text-xs text-slate-400">PNG, JPG, WebP up to 5MB</span>
            </>
          )}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

/* Blog editor — create or edit */
function BlogEditor({ post, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: post?.title || "",
    author_name: post?.author_name || "",
    excerpt: post?.excerpt || "",
    body: post?.body || "",
    cover_image: post?.cover_image || "",
    tags: post?.tags || [],
    published: post?.published ?? false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (publishNow) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.body.trim()) { toast.error("Content body is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, published: publishNow ?? form.published };
      if (post?.id) {
        await api.put(`/admin/blogs/${post.id}`, payload);
        toast.success(payload.published ? "Post published!" : "Draft saved");
      } else {
        await api.post("/admin/blogs", payload);
        toast.success(payload.published ? "Post published!" : "Draft saved");
      }
      onSave();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="blog-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">{post ? "Edit Post" : "New Blog Post"}</h2>
          <p className="text-slate-500 text-sm mt-0.5">{post ? `Editing: ${post.title}` : "Write and publish a new article"}</p>
        </div>
        <Button variant="outline" onClick={onCancel}>← Back</Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
        {/* Main content */}
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div className="space-y-1.5">
              <Label>Post Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.title}
                onChange={e => set("title", e.target.value)}
                placeholder="e.g. How to write a great CV for Indian job market"
                className="text-lg font-semibold h-12"
                data-testid="blog-title-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Excerpt / Summary</Label>
              <Textarea
                value={form.excerpt}
                onChange={e => set("excerpt", e.target.value)}
                placeholder="A short 1-2 sentence summary shown on the blog listing page…"
                rows={2}
                data-testid="blog-excerpt-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Body Content <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.body}
                onChange={e => set("body", e.target.value)}
                placeholder="Write your full article here. Markdown is supported…"
                rows={18}
                className="font-mono text-sm leading-relaxed resize-y"
                data-testid="blog-body-input"
              />
              <p className="text-xs text-slate-400">Tip: Use **bold**, *italic*, # Heading, - lists. Markdown is rendered on the public page.</p>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Publish controls */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Publish</h3>

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Status</span>
              <Badge className={form.published ? "bg-green-100 text-green-700 border-green-200" : "bg-yellow-100 text-yellow-700 border-yellow-200"}>
                {form.published ? "Published" : "Draft"}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="pub-toggle" className="text-sm cursor-pointer">Publish immediately</Label>
              <Switch
                id="pub-toggle"
                checked={form.published}
                onCheckedChange={v => set("published", v)}
                data-testid="blog-publish-toggle"
              />
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <Button
                onClick={() => handleSave(form.published)}
                disabled={saving}
                className="w-full bg-[#1D6FF2] hover:bg-[#1659C7] text-white"
                data-testid="blog-save-btn"
              >
                {saving ? "Saving…" : form.published ? "Save & Publish" : "Save Draft"}
              </Button>
              {!form.published && (
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="w-full"
                  data-testid="blog-publish-now-btn"
                >
                  <Globe className="w-4 h-4 mr-2" /> Publish Now
                </Button>
              )}
            </div>
          </div>

          {/* Cover image */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Cover Image</h3>
            <CoverUpload value={form.cover_image} onChange={v => set("cover_image", v)} />
          </div>

          {/* Author */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Author</h3>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Display name</Label>
              <Input
                value={form.author_name}
                onChange={e => set("author_name", e.target.value)}
                placeholder="Your name…"
                data-testid="blog-author-input"
              />
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide flex items-center gap-2">
              <Tag className="w-4 h-4" /> Tags
            </h3>
            <TagInput tags={form.tags} onChange={v => set("tags", v)} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Blog list + manage */
function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, {} = new, post = edit
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/blogs"); setPosts(r.data); }
    catch { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleEdit = async (post) => {
    // fetch full post (with body) for editor
    try {
      const r = await api.get(`/admin/blogs/${post.id}`);
      setEditing(r.data);
    } catch { toast.error("Could not load post"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const togglePublish = async (post) => {
    try {
      await api.put(`/admin/blogs/${post.id}`, { published: !post.published });
      toast.success(post.published ? "Unpublished" : "Published");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (editing !== null) {
    return (
      <BlogEditor
        post={editing.id ? editing : null}
        onSave={() => { setEditing(null); refresh(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="blog-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Blog Posts</h2>
          <p className="text-slate-500 text-sm mt-0.5">Write articles to help job seekers and employers.</p>
        </div>
        <Button
          onClick={() => setEditing({})}
          className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white"
          data-testid="new-post-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-400">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <p className="font-medium text-slate-600">No blog posts yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-6">Start writing to help your community</p>
          <Button onClick={() => setEditing({})} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white">
            <Plus className="w-4 h-4 mr-2" /> Write First Post
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map(p => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900 max-w-[240px] truncate">{p.title}</div>
                    <div className="text-xs text-slate-400 font-mono">/blog/{p.slug}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.author_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).slice(0, 3).map(t => (
                        <span key={t} className="bg-slate-100 text-slate-600 text-xs px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                    {(p.created_at || "").slice(0, 10)}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{p.views ?? 0}</TableCell>
                  <TableCell>
                    <Badge className={p.published
                      ? "bg-green-100 text-green-700 border-green-200"
                      : "bg-yellow-100 text-yellow-700 border-yellow-200"
                    }>
                      {p.published ? "Published" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title={p.published ? "Unpublish" : "Publish"}
                        onClick={() => togglePublish(p)}
                        data-testid={`toggle-publish-${p.id}`}
                      >
                        {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Edit"
                        onClick={() => handleEdit(p)}
                        data-testid={`edit-post-${p.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Delete"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(p.id)}
                        data-testid={`delete-post-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


function BannerAdmin() {
  const [bannerImage, setBannerImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef();
 
  // Load current banner on mount
  useEffect(() => {
    api.get("/superadmin/settings")
      .then(r => {
        setBannerImage(r.data.banner_image || "");
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);
 
  // Upload image file → get URL back
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBannerImage(res.data.url);
      toast.success("Image uploaded — click Save to apply");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      // reset file input so same file can be re-selected
      if (fileRef.current) fileRef.current.value = "";
    }
  };
 
  const saveBanner = async () => {
    setSaving(true);
    try {
      await api.put("/superadmin/settings", { banner_image: bannerImage });
      toast.success("Banner saved! It will appear on the home page.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };
 
  const removeBanner = async () => {
    setBannerImage("");
    setSaving(true);
    try {
      await api.put("/superadmin/settings", { banner_image: "" });
      toast.success("Banner removed. Default gradient restored.");
    } catch {
      toast.error("Failed to remove");
    } finally {
      setSaving(false);
    }
  };
 
  if (!loaded) return <div className="text-center py-20 text-slate-400">Loading…</div>;
 
  return (
    <div data-testid="banner-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            🖼️ Hero Banner
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Upload a background image for the home page hero section. Recommended: 1920×600px, JPG/PNG/WebP.
          </p>
        </div>
        <div className="flex gap-2">
          {bannerImage && (
            <Button
              variant="outline"
              className="text-red-500 border-red-200 hover:bg-red-50"
              onClick={removeBanner}
              disabled={saving}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </Button>
          )}
          <Button
            onClick={saveBanner}
            disabled={saving}
            className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white"
            data-testid="save-banner-btn"
          >
            <Globe className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save Banner"}
          </Button>
        </div>
      </div>
 
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
 
        {/* Preview */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Live Preview</span>
            <span className="text-xs text-slate-400">How the hero will look</span>
          </div>
 
          {/* Miniature hero preview */}
          <div
            className="relative w-full overflow-hidden"
            style={{
              height: "220px",
              background: bannerImage
                ? `url(${bannerImage}) center center / cover no-repeat`
                : "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 40%, #f5f0ff 100%)",
            }}
          >
            {/* Overlay preview */}
            {bannerImage && (
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 100%)",
              }} />
            )}
            <div className="relative z-10 p-6 flex flex-col justify-center h-full">
              <p className={`text-xs font-bold tracking-widest uppercase mb-2 ${bannerImage ? "text-white/80" : "text-[#1D6FF2]"}`}>
                India's #1 Job Platform
              </p>
              <h1 className={`text-2xl font-extrabold leading-tight mb-1 ${bannerImage ? "text-white" : "text-slate-900"}`}>
                Your job search ends here.
              </h1>
              <p className={`text-sm mb-4 ${bannerImage ? "text-white/80" : "text-slate-500"}`}>
                Discover 50 lakh+ career opportunities across India.
              </p>
              {/* Mock search bar */}
              <div className="bg-white rounded-xl h-10 flex items-center px-4 max-w-sm shadow">
                <span className="text-slate-400 text-xs">Search jobs by title, skill or company...</span>
              </div>
            </div>
          </div>
 
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              {bannerImage
                ? "✅ Custom banner image is set"
                : "ℹ️ No banner set — default gradient will be used"}
            </p>
          </div>
        </div>
 
        {/* Upload controls */}
        <div className="space-y-4">
          {/* Upload box */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Upload Image</h3>
 
            {bannerImage ? (
              <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
                <img src={bannerImage} alt="Current banner" className="w-full h-36 object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="bg-white text-slate-800 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-slate-100"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={removeBanner}
                    className="bg-red-500 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full h-36 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2
                  text-slate-500 hover:border-[#1D6FF2] hover:text-[#1D6FF2] hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <div className="w-6 h-6 border-2 border-[#1D6FF2] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="text-sm font-medium">Click to upload banner</span>
                    <span className="text-xs text-slate-400">JPG, PNG, WebP · Max 5MB</span>
                    <span className="text-xs text-slate-400">Recommended: 1920 × 600 px</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />
          </div>
 
          {/* Or enter URL directly */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-wide">Or Paste Image URL</h3>
            <Input
              value={bannerImage}
              onChange={e => setBannerImage(e.target.value)}
              placeholder="https://example.com/banner.jpg"
              className="text-sm"
              data-testid="banner-url-input"
            />
            <p className="text-xs text-slate-400">
              Paste a direct image URL. Leave empty to restore the default gradient.
            </p>
          </div>
 
          {/* Tips */}
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-700 mb-2">💡 Tips for best results</p>
            <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
              <li>Use a wide landscape image (at least 1200px wide)</li>
              <li>Avoid busy or text-heavy images</li>
              <li>A semi-transparent dark overlay is auto-applied for readability</li>
              <li>After saving, refresh the home page to see the change</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SHELL ───────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const location = useLocation();
  const tab = (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    ? "bg-[#E8F0FE] text-[#1D6FF2]"
    : "text-slate-700 hover:bg-slate-100";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-[240px_1fr] gap-6" data-testid="admin-dashboard">
      <aside className="md:sticky md:top-20 md:self-start">
        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1 text-sm">
          <Link to="/admin"           className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin")}`}><BarChart3    className="w-4 h-4 inline mr-2" />Overview</Link>
          <Link to="/admin/approvals" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/approvals")}`} data-testid="tab-approvals"><CheckSquare  className="w-4 h-4 inline mr-2" />Job Approvals</Link>
          <Link to="/admin/companies" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/companies")}`} data-testid="tab-companies"><Building2    className="w-4 h-4 inline mr-2" />Companies</Link>
          <Link to="/admin/cities"    className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/cities")}`}    data-testid="tab-cities"   ><Building2    className="w-4 h-4 inline mr-2" />Cities</Link>
          <Link to="/admin/categories"className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/categories")}`}data-testid="tab-categories"><Layers       className="w-4 h-4 inline mr-2" />Categories</Link>
          <Link to="/admin/pricing"   className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/pricing")}`}   data-testid="tab-pricing"  ><IndianRupee  className="w-4 h-4 inline mr-2" />Pricing</Link>
          <Link to="/admin/coupons"   className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/coupons")}`}   data-testid="tab-coupons"  ><Ticket       className="w-4 h-4 inline mr-2" />Coupons</Link>
          <div className="border-t my-2" />
          <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">CRM</div>
          <Link to="/admin/crm/newfeed" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/crm/newfeed")}`} data-testid="tab-crm-newfeed"><Users2 className="w-4 h-4 inline mr-2" />New Feed CRM</Link>
          <Link to="/admin/crm/coupon"  className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/crm/coupon")}`}  data-testid="tab-crm-coupon"><Ticket className="w-4 h-4 inline mr-2" />Coupon Code CRM</Link>
          <Link to="/admin/crm/normal"  className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/crm/normal")}`}  data-testid="tab-crm-normal"><Contact2 className="w-4 h-4 inline mr-2" />Normal CRM</Link>
          <Link to="/admin/coupon-batches" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/coupon-batches")}`} data-testid="tab-coupon-batches"><Layers className="w-4 h-4 inline mr-2" />Coupon Batches</Link>
          <Link to="/admin/users"     className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/users")}`}     data-testid="tab-users"    ><Users        className="w-4 h-4 inline mr-2" />Users</Link>
          <Link to="/admin/reports"   className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/reports")}`}                              ><FileWarning  className="w-4 h-4 inline mr-2" />Reports</Link>
          <div className="border-t border-slate-100 my-1" />
          <Link to="/admin/blogs"     className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/blogs")}`}     data-testid="tab-blogs"    ><BookOpen     className="w-4 h-4 inline mr-2" />Blog Posts</Link>
          <Link to="/admin/whatsapp" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/whatsapp")}`} data-testid="tab-whatsapp"><MessageCircle className="w-4 h-4 inline mr-2" />WhatsApp FAQ</Link>
          <Link to="/admin/banner" className={`block px-3 py-2 rounded-lg font-medium ${tab("/admin/banner")}`} data-testid="tab-banner">Hero Banner</Link>
        </div>
      </aside>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="approvals"  element={<Approvals />} />
        <Route path="companies"  element={<CompaniesAdmin />} />
        <Route path="cities"     element={<Cities />} />
        <Route path="categories" element={<Categories />} />
        <Route path="pricing"    element={<PricingAdmin />} />
        <Route path="coupons"    element={<CouponsAdmin />} />
        <Route path="crm/newfeed" element={<CrmPanel mode="newfeed" />} />
        <Route path="crm/coupon"  element={<CrmPanel mode="coupon" />} />
        <Route path="crm/normal"  element={<CrmPanel mode="normal" />} />
        <Route path="coupon-batches" element={<CouponBatches />} />
        <Route path="users"      element={<UsersList />} />
        <Route path="reports"    element={<Reports />} />
        <Route path="blogs"      element={<BlogAdmin />} />
        <Route path="whatsapp"   element={<WhatsAppFAQAdmin />} /> 
        <Route path="banner"    element={<BannerAdmin />} />
      </Routes>
    </div>
  );
}